import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import PropTypes from 'prop-types';
import qs from 'query-string';

import { SiteConfigManager, useCategoryState, useSportState } from '@atlas/core';
import { Button, Label, Tab, TabContent, TabNavigator, useTabNavigator } from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import MarketTypeSelector from 'common/MarketTypeSelector/MarketTypeSelector';
import categoryShape from 'common/PropTypes/categoryShape';
import SeoPageText from 'components/SeoPage/SeoPageText';

import CategoriesList from './CategoriesList';
import CategoryTournamentsList from './CategoryTournamentsList';

const TAB_QUERY_VALUES = {
  events: 'events',
  top: 'top',
};

const TABS_INDEX = {
  events: 1,
  top: 0,
};

const AllEventsButton = () => {
  const [, setActiveIndex] = useTabNavigator();
  const { push: historyPush } = useHistory();

  const onClickHandler = useCallback(() => {
    // Using special implementation to fix safari scroll issue
    const layoutWrapper = document.querySelector('.layout-wrapper');
    if (layoutWrapper) {
      const sportscroll = document.querySelector('.sport-scroll');
      if (sportscroll) {
        layoutWrapper.scrollTo(0, sportscroll.offsetTop);
      }
    }

    setActiveIndex(TABS_INDEX.events);
    historyPush(`?tab=${TAB_QUERY_VALUES.events}`);
  }, [historyPush, setActiveIndex]);

  return (
    <div className="sticky-bottom-buttons-container button-all-events">
      <Button label="Show all events" onClick={onClickHandler} size="big" type="secondary" />
    </div>
  );
};

const TabsList = () => {
  const [activeIndex, setActiveIndex] = useTabNavigator();
  const { push: historyPush } = useHistory();
  const [wasClicked, setWasClicked] = useState(false);

  const tabClickHandler = useCallback(
    queryValue => {
      historyPush(`?tab=${queryValue}`);
      setWasClicked(true);
    },
    [historyPush],
  );

  const clickOnActiveTabHandler = useCallback(
    index => {
      if (activeIndex === index) {
        const toggleMap = {
          [TABS_INDEX.top]: { index: TABS_INDEX.events, query: TAB_QUERY_VALUES.events },
          [TABS_INDEX.events]: { index: TABS_INDEX.top, query: TAB_QUERY_VALUES.top },
        };

        const toggle = toggleMap[index];
        if (toggle) {
          setActiveIndex(toggle.index);
          historyPush(`?tab=${toggle.query}`);
        }
      }
    },
    [activeIndex, historyPush, setActiveIndex],
  );

  return (
    <div className={cn('tabs-list mobile-sport-tabs', { clicked: wasClicked })}>
      <Tab
        className="mobile-tab"
        clickCb={() => tabClickHandler(TAB_QUERY_VALUES.top)}
        index={TABS_INDEX.top}
      >
        {/* mobile-tab-inner-wrapper div is a fix for click on active tab */}
        <div
          className="mobile-tab-inner-wrapper"
          onClick={() => clickOnActiveTabHandler(TABS_INDEX.top)}
        >
          <div className="mobile-tab-content">
            <div
              className="mobile-tab-content-icon"
              style={{
                backgroundImage: `url("${BASE_PATH}/static/assets/icons/icon-emoji-fire.png")`,
              }}
            />
            {!activeIndex && <Label message="top_tournaments" />}
          </div>
        </div>
      </Tab>

      <Tab
        className="mobile-tab"
        clickCb={() => tabClickHandler(TAB_QUERY_VALUES.events)}
        index={TABS_INDEX.events}
      >
        <div
          className="mobile-tab-inner-wrapper"
          onClick={() => clickOnActiveTabHandler(TABS_INDEX.events)}
        >
          <div className="mobile-tab-content">
            <div
              className="mobile-tab-content-icon"
              style={{
                backgroundImage: `url("${BASE_PATH}/static/assets/icons/icon-emoji-cup.png")`,
              }}
            />
            {!!activeIndex && <Label message="all_events" />}
          </div>
        </div>
      </Tab>
    </div>
  );
};

const PrematchTopTournamentsList = ({
  categories,
  primaryCategoryUrlName,
  primaryTournamentUrlName,
  search,
}) => {
  const { mainMarketTypes } = useSportState();
  const { tournaments } = useCategoryState();
  const parsedQuery = qs.parse(search);
  const hasTopTabSearch = parsedQuery.tab === TAB_QUERY_VALUES.top;
  const hasEventsTabSearch = parsedQuery.tab === TAB_QUERY_VALUES.events;

  const mainMarketsChoice = SiteConfigManager.getConfig(
    'config.config.sportsbook.mainMarketsChoice',
  );
  const mobileCollapseEvents = SiteConfigManager.getConfig(
    'config.config.sportsbook.mobileCollapseEvents',
  );

  const openEventsByDefault = SiteConfigManager.getConfig(
    'config.config.sportsbook.mobileOpenEventsByDefault',
    false,
  );

  const activeIndex =
    hasEventsTabSearch || (!hasTopTabSearch && openEventsByDefault)
      ? TABS_INDEX.events
      : TABS_INDEX.top;

  return (
    <div className="category-container">
      <TabNavigator index={activeIndex}>
        <TabsList />

        <TabContent index={TABS_INDEX.top}>
          {mainMarketsChoice && <MarketTypeSelector />}
          <h2 className="d-none">{tournaments[0]?.categoryName}</h2>
          <div className="categories">
            <CategoryTournamentsList
              // TODO: Should be removed in next updates
              clickedCategory={null}
              isTopCompetitions
              mainMarketTypes={mainMarketTypes}
              primaryCategoryUrlName={primaryCategoryUrlName}
              primaryTournamentUrlName={primaryTournamentUrlName}
            />
          </div>
          <AllEventsButton />
          <SeoPageText />
        </TabContent>

        <TabContent index={TABS_INDEX.events}>
          <CategoriesList
            categories={categories}
            forceIsExpanded={!mobileCollapseEvents}
            primaryCategoryUrlName={primaryCategoryUrlName}
            primaryTournamentUrlName={primaryTournamentUrlName}
          />
        </TabContent>
      </TabNavigator>
    </div>
  );
};

PrematchTopTournamentsList.propTypes = { categories: PropTypes.arrayOf(categoryShape).isRequired };

export default PrematchTopTournamentsList;
